import {
    FormatAs,
    PanelStaticHeader,
    StaticFieldModalConfig,
    StaticPanelHeaderLinkConfig
} from "../../DynamicFormTypes.ts";
import React from "react";
import {useFormMethods} from "../../DyanmicFormContext.tsx";
import {useWatch} from "react-hook-form";
import {defaultParserForSelects} from "../../dyanimcFormUtil.tsx";
import {StaticFieldModalWrapper} from "../../modals/StaticFieldModalWrapper.tsx";

// Format handlers for each FormatAs type
const formatHandlers = {
    [FormatAs.CURRENCY]: {
        render: (value: any) => {
            const parsedValue = defaultParserForSelects(value);
            value = parsedValue && parsedValue?.label ? parsedValue.label : value;
            return (
                <span>
                    {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Number(value))}
                </span>
            );
        }
    },

    [FormatAs.PERCENTAGE]: {
        render: (value: any) => {
            const parsedValue = defaultParserForSelects(value);
            value = parsedValue && parsedValue?.label ? parsedValue.label : value;
            return <span>{value}%</span>;
        }
    },

    [FormatAs.LINK]: {
        parse: (value: any) => {
            let parsedValue: any = value;

            if (typeof value === 'string' && value.includes("{")) {
                try {
                    parsedValue = JSON.parse(value);
                } catch (error) {
                    console.error("Error parsing JSON string:", error);
                }
            }

            if (!parsedValue.href) {
                return {
                    href: parsedValue,
                    display: parsedValue
                };
            }

            return parsedValue;
        },
        render: (value: any) => {
            let linkConfig: StaticPanelHeaderLinkConfig
            try {
                linkConfig = typeof value === "string" ? JSON.parse(value) : value;
            } catch (error) {
                console.error("Error parsing JSON string:", error);
                return null;
            }

            return (
                <a
                    href={linkConfig.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-indigo-600 hover:text-indigo-800 hover:underline cursor-pointer"
                    onClick={(e) => {
                        e.stopPropagation();
                    }}
                >
                    {linkConfig.display}
                </a>
            );
        }
    },

    [FormatAs.MODAL]: {
        render: (value: any, staticHeader: PanelStaticHeader) => {
            let modalConfig: StaticFieldModalConfig;
            try {
                modalConfig = typeof value === "string" ? JSON.parse(value) : value;
            } catch (error) {
                console.error("Error parsing JSON string:", error);
                return null;
            }

            return (
                <StaticFieldModalWrapper
                    modalConfig={modalConfig}
                    fieldKey={staticHeader.fieldKey}
                />
            );
        }
    },

    [FormatAs.NONE]: {
        parse: (value: any) => value,
        render: (value: any) => {
            return <span>{value}</span>;
        }
    }
};

interface PanelStaticHeaderProps {
    staticHeader: PanelStaticHeader;
}

const PanelStaticHeaderWatchedRenderer: React.FC<PanelStaticHeaderProps> = ({ staticHeader }) => {
    const { formMethods } = useFormMethods()

    // Watch for changes to this field
    const value = useWatch({
        control: formMethods.control,
        name: staticHeader.fieldKey,
    });

    // Format the static header value
    const formatted = React.useMemo(() => {
        const formatAs = staticHeader.formatAs ?? FormatAs.NONE;
        const handler: any = formatHandlers[formatAs as keyof typeof formatHandlers];
        return handler ? handler.render(value, staticHeader) : value ?? "";
    }, [value, staticHeader]);


    return (
        <div className="flex items-center">
            <span className="text-gray-900">
                {formatted}
            </span>
        </div>
    );
}

export const PanelStaticHeaderWatched = React.memo(PanelStaticHeaderWatchedRenderer);